import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, EmptyState } from "../components/ui-kit";
import { CheckCircle2, XCircle, Receipt, FileDown, X, Camera, Gauge, FileImage } from "lucide-react";
import { toast } from "sonner";
import { LazyPhoto } from "../components/LazyPhoto";

const STATUS_STYLE = {
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

export default function ExpenseApprovalsPage() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [lightbox, setLightbox] = useState(null); // {src, title}
  const [invoiceCache, setInvoiceCache] = useState({}); // sessionId → blobUrl

  const load = async () => {
    const [p, h] = await Promise.all([
      api.get("/expenses/pending"),
      api.get("/expenses/review-history"),
    ]);
    setPending(p.data || []);
    setHistory(h.data || []);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (sid) => {
    try {
      await api.post(`/expenses/${sid}/approve`, { remarks: "" });
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to approve");
    }
  };

  const rows = tab === "pending" ? pending : history;
  const totalPending = pending.reduce((a, r) => a + Number(r.expense_total || 0), 0);

  return (
    <div>
      <PageHeader title="Expense Approvals" subtitle="Review and approve your team's field expense claims" />

      <div className="flex flex-wrap gap-3 mb-6">
        <Card className="flex-1 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Pending claims</div>
          <div className="text-3xl font-heading font-black text-amber-700" data-testid="pending-count">{pending.length}</div>
        </Card>
        <Card className="flex-1 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Pending amount</div>
          <div className="text-3xl font-heading font-black text-[#1E3F20]">₹{totalPending.toFixed(0)}</div>
        </Card>
      </div>

      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setTab("pending")}
          data-testid="tab-pending"
          className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "pending" ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white text-[#1E3F20] border-[#E5E2DC]"}`}
        >
          Pending ({pending.length})
        </button>
        <button
          onClick={() => setTab("history")}
          data-testid="tab-history"
          className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "history" ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white text-[#1E3F20] border-[#E5E2DC]"}`}
        >
          History ({history.length})
        </button>
      </div>

      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState title="Nothing here" description={tab === "pending" ? "No pending claims to review." : "No approved / rejected claims yet."} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Sales person</th>
                  <th className="px-4 py-3">KM</th>
                  <th className="px-4 py-3">Travel</th>
                  <th className="px-4 py-3">DA</th>
                  <th className="px-4 py-3">Night</th>
                  <th className="px-4 py-3">Other</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Photos</th>
                  {tab === "pending" ? (
                    <th className="px-4 py-3 text-right">Action</th>
                  ) : (
                    <th className="px-4 py-3">Status</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E2DC]" data-testid={`approval-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold">{r.date_key}</td>
                    <td className="px-4 py-3">{r.user_name}</td>
                    <td className="px-4 py-3">{r.km_covered ?? 0}</td>
                    <td className="px-4 py-3">₹{Number(r.travel_amount || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">₹{Number(r.daily_allowance || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">₹{Number(r.night_stay_amount || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">₹{Number(r.other_amount || 0).toFixed(0)}</td>
                    <td className="px-4 py-3 font-bold text-[#1E3F20]">₹{Number(r.expense_total || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">
                      <PhotoStrip
                        session={r}
                        invoiceCache={invoiceCache}
                        setInvoiceCache={setInvoiceCache}
                        onOpen={(src, title) => setLightbox({ src, title })}
                      />
                    </td>
                    {tab === "pending" ? (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setViewing(r)}
                            className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold"
                            data-testid={`view-${r.id}`}
                          >
                            View
                          </button>
                          <button
                            onClick={() => approve(r.id)}
                            className="text-xs px-2.5 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-semibold inline-flex items-center gap-1"
                            data-testid={`approve-${r.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => setRejecting(r)}
                            className="text-xs px-2.5 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 font-semibold inline-flex items-center gap-1"
                            data-testid={`reject-${r.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      </td>
                    ) : (
                      <td className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${STATUS_STYLE[r.expense_status]}`}>
                              {r.expense_status}
                            </span>
                            {r.expense_reviewed_by_name && (
                              <div className="text-[11px] text-[#5C6B5E] mt-1">By {r.expense_reviewed_by_name}</div>
                            )}
                            {r.expense_review_remarks && (
                              <div className="text-[11px] text-[#5C6B5E] mt-1">Note: {r.expense_review_remarks}</div>
                            )}
                          </div>
                          <button
                            onClick={() => setViewing(r)}
                            className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold whitespace-nowrap"
                            data-testid={`history-view-${r.id}`}
                          >
                            View · PDF
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {viewing && <ClaimDetailModal session={viewing} onClose={() => setViewing(null)} />}
      {rejecting && (
        <RejectModal
          session={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            load();
          }}
        />
      )}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="approval-lightbox"
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-200"
              data-testid="approval-lightbox-close"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-white mb-2 text-sm font-semibold">{lightbox.title}</div>
            <img src={lightbox.src} alt="Expense attachment" className="w-full max-h-[85vh] object-contain rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function PhotoStrip({ session, invoiceCache, setInvoiceCache, onOpen }) {
  const [invoicePreview, setInvoicePreview] = useState(invoiceCache[session.id] || "");

  useEffect(() => {
    let cancelled = false;
    let blobUrl = "";
    if (session.night_stay_invoice_path && !invoiceCache[session.id]) {
      (async () => {
        try {
          const res = await api.get("/expenses/invoice", {
            params: { path: session.night_stay_invoice_path },
            responseType: "blob",
          });
          blobUrl = URL.createObjectURL(res.data);
          if (!cancelled) {
            setInvoicePreview(blobUrl);
            setInvoiceCache((c) => ({ ...c, [session.id]: blobUrl }));
          }
        } catch (err) {
          logger.debug("invoice thumb load failed:", err?.message);
        }
      })();
    }
    return () => {
      cancelled = true;
      // Blob URLs are cached by parent — don't revoke here or thumbs disappear on rerender.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id, session.night_stay_invoice_path]);

  const items = [
    { key: "start", label: "Start KM", lazy: session.has_start_km_photo || !!session.start_km_photo_b64, kind: "session_start", src: session.start_km_photo_b64, icon: Gauge },
    { key: "end", label: "End KM", lazy: session.has_end_km_photo || !!session.end_km_photo_b64, kind: "session_end", src: session.end_km_photo_b64, icon: Gauge },
    { key: "invoice", label: "Night stay invoice", src: invoicePreview, icon: FileImage, path: session.night_stay_invoice_path },
  ];
  const anyExists = items.some((it) => it.src || it.path || it.lazy);
  if (!anyExists) return <span className="text-xs text-[#8A8577]">—</span>;

  return (
    <div className="flex items-center gap-1.5" data-testid={`approval-thumbs-${session.id}`}>
      {items.map(({ key, label, src, icon: Icon, path, lazy, kind }) => {
        if (lazy && kind) {
          return (
            <LazyPhoto
              key={key}
              kind={kind}
              id={session.id}
              alt={label}
              title={`${label} — ${session.user_name} · ${session.date_key}`}
              className="w-10 h-10 rounded border border-[#E5E2DC] hover:border-[#1E3F20]/50"
              onOpen={(url, title) => onOpen(url, title)}
              testid={`approval-thumb-${key}-${session.id}`}
            />
          );
        }
        if (!src && !path) {
          return (
            <div
              key={key}
              title={`${label} — not attached`}
              className="w-10 h-10 rounded border border-dashed border-[#E5E2DC] grid place-items-center text-[#C7C0B0]"
            >
              <Icon className="w-3.5 h-3.5" />
            </div>
          );
        }
        if (!src && path) {
          // Invoice still loading
          return (
            <div
              key={key}
              title={`${label} — loading…`}
              className="w-10 h-10 rounded border border-[#E5E2DC] grid place-items-center bg-[#F2EDE4] text-[#5C6B5E]"
            >
              <Icon className="w-3.5 h-3.5 animate-pulse" />
            </div>
          );
        }
        return (
          <button
            key={key}
            type="button"
            onClick={() => onOpen(src, `${label} — ${session.user_name} · ${session.date_key}`)}
            className="group relative w-10 h-10 rounded border border-[#E5E2DC] overflow-hidden hover:border-[#1E3F20]/50"
            title={label}
            data-testid={`approval-thumb-${key}-${session.id}`}
          >
            <img src={src} alt={label} className="w-full h-full object-cover" />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          </button>
        );
      })}
    </div>
  );
}

function ClaimDetailModal({ session, onClose }) {
  const [imgSrc, setImgSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    let blobUrl = "";
    (async () => {
      if (!session.night_stay_invoice_path) return;
      try {
        const res = await api.get("/expenses/invoice", { params: { path: session.night_stay_invoice_path }, responseType: "blob" });
        blobUrl = URL.createObjectURL(res.data);
        if (!cancelled) setImgSrc(blobUrl);
      } catch (err) {
        // Invoice not found or session expired — show 'Loading invoice…' fallback.
        logger.debug("invoice load failed:", err?.message);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="claim-detail-modal">
        <div className="p-5 border-b border-[#E5E2DC] flex items-center justify-between">
          <div className="font-heading text-xl font-bold flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#1E3F20]" /> Claim — {session.user_name} · {session.date_key}
          </div>
          <button onClick={onClose} className="text-[#5C6B5E]">×</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <Row label="Total KM" value={session.km_covered ?? 0} />
          <Row label="Rate per km" value={`₹${Number(session.rate_per_km || 0).toFixed(2)}`} />
          <Row label="Travel (auto)" value={`₹${Number(session.travel_amount || 0).toFixed(2)}`} />
          <Row label="Daily allowance" value={`₹${Number(session.daily_allowance || 0).toFixed(2)}`} />
          <Row label="Night stay" value={`₹${Number(session.night_stay_amount || 0).toFixed(2)}`} />
          <Row label="Other" value={`₹${Number(session.other_amount || 0).toFixed(2)}`} />
          {session.other_remarks && <Row label="Other remarks" value={session.other_remarks} full />}
          <Row label="Total claim" value={`₹${Number(session.expense_total || 0).toFixed(2)}`} bold full />
          {session.night_stay_invoice_path && (
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold mb-2">Night stay invoice</div>
              {imgSrc ? (
                <img src={imgSrc} alt="Invoice" className="max-h-80 rounded border border-[#E5E2DC]" data-testid="invoice-img" />
              ) : (
                <div className="text-xs text-[#5C6B5E]">Loading invoice…</div>
              )}
            </div>
          )}
        </div>
        <div className="p-5 border-t border-[#E5E2DC] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={async () => {
              try {
                const res = await api.get(`/expenses/${session.id}/pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `expense_${session.user_name || session.id}_${session.date_key || ""}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch {
                toast.error("Download failed");
              }
            }}
            data-testid="claim-pdf"
          >
            <FileDown className="w-4 h-4" /> Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">{label}</div>
      <div className={bold ? "text-2xl font-heading font-black text-[#1E3F20]" : "text-sm"}>{value}</div>
    </div>
  );
}

function RejectModal({ session, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!remarks.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      await api.post(`/expenses/${session.id}/reject`, { remarks });
      toast.success("Claim rejected");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to reject");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
        <div className="p-5 border-b border-[#E5E2DC] font-heading text-xl font-bold">Reject claim</div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-[#5C6B5E]">Rejecting <b>{session.user_name}</b>'s claim of <b>₹{Number(session.expense_total || 0).toFixed(0)}</b> for {session.date_key}.</div>
          <div>
            <div className="text-sm font-semibold mb-1">Reason (required)</div>
            <textarea
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              data-testid="reject-remarks"
              className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
            />
          </div>
        </div>
        <div className="p-5 border-t border-[#E5E2DC] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="reject-submit">
            <XCircle className="w-4 h-4" /> {busy ? "Rejecting…" : "Reject"}
          </Button>
        </div>
      </div>
    </div>
  );
}
